import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { isNetworkError } from '../../utils/api/networkErrors';
import type { UserSkill } from '../../types/user';
import { useAuth } from '../auth/useAuth';

// Mock data for offline fallback
const MOCK_SKILLS: UserSkill[] = [
  {
    name: 'JavaScript',
    level: 'intermediate',
    lastUpdated: new Date()
  },
  {
    name: 'React',
    level: 'intermediate',
    lastUpdated: new Date()
  }
];

export function useUserSkills() {
  const [skills, setSkills] = useState<UserSkill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  const loadUserSkills = async (isRetry = false) => {
    try {
      if (!user) {
        setSkills([]);
        return;
      }

      // Check if offline
      if (!navigator.onLine) {
        setSkills(MOCK_SKILLS);
        toast.error('You are offline. Using cached data.', { id: 'offline-skills' });
        return;
      }

      // Try to load from user_skill_assessments first
      try {
        const { data, error } = await supabase
          .from('user_skill_assessments')
          .select('*')
          .eq('user_id', user?.id)
          .order('skill_name');

        if (!error && data) {
          setSkills(
            data.map(skill => ({
              name: skill.skill_name,
              level: skill.proficiency_level,
              lastUpdated: new Date(skill.last_assessed || skill.created_at)
            }))
          );
          return; // Success, so return early
        }
      } catch (err) {
        console.log('Could not load from user_skill_assessments, trying user_skills');
      }

      // Fallback to user_skills
      const { data, error } = await supabase
        .from('user_skills')
        .select('*')
        .eq('user_id', user?.id)
        .order('skill_name');

      if (error) throw error;

      // Map according to user_skills schema
      setSkills(
        data.map(skill => ({
          name: skill.skill_name,
          level: skill.level, // Different column name in user_skills
          lastUpdated: new Date(skill.last_updated || skill.created_at) // Different column name
        }))
      );
    } catch (error) {
      console.error('Error loading user skills:', error);
      
      if (isNetworkError(error)) {
        if (retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          setTimeout(() => loadUserSkills(true), RETRY_DELAY * Math.pow(2, retryCount));
          if (!isRetry) {
            toast.error('Connection error. Retrying...', { id: 'skills-retry' });
          }
        } else {
          setSkills(MOCK_SKILLS);
          toast.error('Using offline data. Please check your connection.');
        }
      } else {
        toast.error('Failed to load skills');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserSkills();
  }, [user]);

  const addSkills = async (newSkills: string[], level: UserSkill['level'] = 'beginner') => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Try to insert to user_skills
      const { error } = await supabase
        .from('user_skills')
        .upsert(
          newSkills.map(skillName => ({
            user_id: user.id,
            skill_name: skillName,
            level: level, // user_skills uses 'level'
            last_updated: new Date().toISOString() // user_skills uses 'last_updated'
          })),
          { onConflict: 'user_id,skill_name' }
        );

      if (error) {
        console.error('Error adding to user_skills, will try user_skill_assessments', error);
        
        // Try user_skill_assessments as fallback
        const { error: assessError } = await supabase
          .from('user_skill_assessments')
          .upsert(
            newSkills.map(skillName => ({
              user_id: user.id,
              skill_name: skillName,
              proficiency_level: level, // user_skill_assessments uses 'proficiency_level'
              last_assessed: new Date().toISOString() // user_skill_assessments uses 'last_assessed'
            })),
            { onConflict: 'user_id,skill_name' }
          );
          
        if (assessError) throw assessError;
      }

      await loadUserSkills();
      toast.success('Skills updated successfully!');
    } catch (error) {
      console.error('Error adding skills:', error);
      if (isNetworkError(error)) {
        toast.error('Network error. Please try again.');
        return;
      }
      toast.error('Failed to add skills');
      throw error;
    }
  };

  const updateSkill = async (skillName: string, newLevel: UserSkill['level']) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Try to update in user_skills
      try {
        const { error } = await supabase
          .from('user_skills')
          .update({
            level: newLevel, // user_skills uses 'level'
            last_updated: new Date().toISOString() // user_skills uses 'last_updated'
          })
          .eq('user_id', user.id)
          .eq('skill_name', skillName);

        if (!error) {
          await loadUserSkills();
          toast.success('Skill updated successfully!');
          return; // Success, so return early
        }
      } catch (err) {
        console.log('Could not update user_skills, trying user_skill_assessments');
      }

      // Try user_skill_assessments as fallback
      const { error } = await supabase
        .from('user_skill_assessments')
        .update({
          proficiency_level: newLevel, // user_skill_assessments uses 'proficiency_level'
          last_assessed: new Date().toISOString() // user_skill_assessments uses 'last_assessed'
        })
        .eq('user_id', user.id)
        .eq('skill_name', skillName);

      if (error) throw error;

      await loadUserSkills();
      toast.success('Skill updated successfully!');
    } catch (error) {
      console.error('Error updating skill:', error);
      if (isNetworkError(error)) {
        toast.error('Network error. Please try again.');
        return;
      }
      toast.error('Failed to update skill');
      throw error;
    }
  };

  const deleteSkill = async (skillName: string) => {
    try {
      if (!user) {
        toast.error('Please sign in to continue');
        return;
      }

      // Delete from user_skill_assessments
      const { error: deleteError } = await supabase
        .from('user_skill_assessments')
        .delete()
        .eq('user_id', user.id)
        .eq('skill_name', skillName);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }

      // Update local state immediately for better UX
      setSkills(prevSkills => prevSkills.filter(skill => skill.name !== skillName));
      toast.success('Skill deleted successfully!');
      
      // Refresh skills list from server
      await loadUserSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
      if (isNetworkError(error)) {
        toast.error('Network error. Please try again.');
        return;
      }
      toast.error('Failed to delete skill. Please try again.');
    }
  };

  return {
    skills,
    isLoading,
    addSkills,
    updateSkill,
    deleteSkill,
    refreshSkills: loadUserSkills
  };
}